/**
 * One admin, branded per endpoint: Issa on Instant Systems, Maia on Maikoz.
 * Every surface difference between the two is declared in PROFILES rather than
 * branched at the call site.
 *
 * Resolved with the brand (`resolveBrand` in brand.ts): a SuperAdmin pick, then
 * the endpoint, then the host. The endpoint outranks the host because a Maikoz
 * tenant is reachable from either cockpit and the operator should meet the same
 * assistant both ways.
 */

import { useResolvedBrand } from "./brandSync.tsx";
import { resolveBrand, type BrandId } from "./brand.ts";
import issaFace from "../../assets/issa.svg";
import maiaFace from "../../assets/maia.png";

export interface AssistantProfile {
  /** Display name only — the backend service is the same for both editions.
   *  Title-cased like a name, never an acronym (see AGENTS.md, Issa copy). */
  name: string;
  /** Square portrait. Cropped to a circle by every consumer. */
  face: string;
  /** A cut-out portrait needs a ring to read as an avatar; one drawn on its own
   *  ground already does, and a ring in the brand colour would vanish into it. */
  ring: { color: string } | null;
  /** Model configuration this assistant answers through. Her persona lives in
   *  that configuration on the AI side, so it is what makes her sound like
   *  herself. Used wherever the Analyst model would otherwise be picked; an
   *  explicit choice in the model picker still wins. Null keeps the endpoint's
   *  own model. */
  modelConfigId: string | null;
  /** Which animated figure stands for her in the chat dock and beside answers. */
  figure: "issa" | "maia";
}

/** Maia's model configuration on the AI side: a "maia" row in InstantAI's
 *  Settings table. `system_prompt_maia.md` is never seeded automatically, and an
 *  id InstantAI does not know falls back to default, i.e. Issa — quietly. */
const MAIA_MODEL_CONFIG_ID: string | null = "maia";

const ISSA: AssistantProfile = {
  name: "Issa",
  face: issaFace,
  ring: { color: "#c62625" },
  modelConfigId: null,
  figure: "issa",
};

const PROFILES: Record<BrandId, AssistantProfile> = {
  instantsystems: ISSA,
  ctlp: ISSA,
  maikoz: {
    name: "Maia",
    face: maiaFace,
    ring: null,
    modelConfigId: MAIA_MODEL_CONFIG_ID,
    figure: "maia",
  },
};

export function assistantForBrand(brand: BrandId): AssistantProfile {
  return PROFILES[brand];
}

/** Falls through to Issa, so an unbranded endpoint is never renamed by
 *  accident. `pack` is the host's theme pack. */
export function resolveAssistantProfile(
  endpointId: number | null,
  pack: string | null,
): AssistantProfile {
  return PROFILES[resolveBrand({ endpointId, hostPack: pack, hostTitle: "" }).id];
}

export function useAssistant(): AssistantProfile {
  return PROFILES[useResolvedBrand().id];
}
